import { useCallback } from 'react';
import AdminAuthService from '../services/AdminAuthService';
import {
  DEFAULT_STORY_COACH_ANSWERS,
  MODULE_LIMITS,
  applyStoryCoachAnswersToStory,
  createStoryModule,
  getShareSettingsForMode,
  getStoryCoachModuleData,
  isHiddenMainFlowModule,
} from '../services/giftBuilderConfig';
import { MEDIA_ITEM_LIMITS, PAGE_MEDIA_LIMITS, countPageMedia } from '../services/mediaLimits';
import { useFeedback } from '../context/FeedbackContext';

const DATA_SCHEMA_VERSION = 2;

const sanitizeForFirestore = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForFirestore(item));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, child]) => {
      if (child !== undefined) {
        acc[key] = sanitizeForFirestore(child);
      }
      return acc;
    }, {});
  }
  return value;
};

const trimMediaCollections = (data = {}) => {
  if (!data || typeof data !== 'object') return data;
  const nextData = { ...data };

  if (Array.isArray(nextData.images)) {
    nextData.images = nextData.images.slice(0, MEDIA_ITEM_LIMITS.images);
  }
  if (Array.isArray(nextData.photos)) {
    nextData.photos = nextData.photos.slice(0, MEDIA_ITEM_LIMITS.images);
  }
  if (Array.isArray(nextData.videos)) {
    nextData.videos = nextData.videos.slice(0, MEDIA_ITEM_LIMITS.videos);
  }
  if (Array.isArray(nextData.videoItems)) {
    nextData.videoItems = nextData.videoItems.slice(0, MEDIA_ITEM_LIMITS.videos);
  }
  if (Array.isArray(nextData.audios)) {
    nextData.audios = nextData.audios.slice(0, MEDIA_ITEM_LIMITS.audios);
  }
  if (Array.isArray(nextData.audioItems)) {
    nextData.audioItems = nextData.audioItems.slice(0, MEDIA_ITEM_LIMITS.audios);
  }

  return nextData;
};

const enforceMediaLimits = (giftData = {}) => ({
  ...giftData,
  modules: (giftData.modules || []).map((module) => ({
    ...module,
    data: trimMediaCollections(module.data || {}),
  })),
});

const getMediaCleanupMessage = (mediaCleanup) => {
  if (!mediaCleanup) return '';
  if (mediaCleanup.skipped) return '';
  const deletedCount = Number(mediaCleanup.deletedCount || 0);
  const failedCount = Number(mediaCleanup.failedCount || 0);
  if (failedCount > 0) {
    return ` Saved, but ${failedCount} unused media file${failedCount === 1 ? '' : 's'} could not be cleaned up automatically.`;
  }
  if (deletedCount > 0) {
    return ` Cleaned up ${deletedCount} unused media file${deletedCount === 1 ? '' : 's'}.`;
  }
  return ' No unused media needed cleanup.';
};

export const useGiftActions = ({
  pageId,
  pageData,
  setPageData,
  currentThemeId,
  setIsLoading,
  setIsLocked,
  setIsEditing,
  setIsPreviewMode,
  moduleEndRef,
  deleteInput,
  isUploadActive = false,
  onAdminSessionExpired,
  onGiftDeleted,
}) => {
  const feedback = useFeedback();

  const scrollToModuleEnd = useCallback(() => {
    setTimeout(() => moduleEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
  }, [moduleEndRef]);

  const updateModule = useCallback((id, data) => {
    const nextData = data && typeof data === 'object' ? { ...data, __touched: true } : data;
    setPageData((prev) => ({
      ...prev,
      modules: prev.modules.map((module) => module.id === id ? {
        ...module,
        data: nextData && typeof nextData === 'object' ?
          { ...(module.data || {}), ...nextData } :
          nextData,
      } : module),
    }));
  }, [setPageData]);

  const removeModule = useCallback((id) => {
    setPageData((prev) => ({
      ...prev,
      modules: prev.modules.filter((module) => module.id !== id),
    }));
  }, [setPageData]);

  const saveGift = useCallback(async (lockGift = false) => {
    if (isUploadActive) {
      feedback.toast({
        type: 'warning',
        title: 'Upload In Progress',
        message: 'Please wait until the media upload finishes, then save again.',
      });
      return;
    }
    setIsLoading(true);
    let saveStep = lockGift ? 'saving gift content' : 'saving draft';
    try {
      const limitedPageData = enforceMediaLimits({
        ...pageData,
        schemaVersion: DATA_SCHEMA_VERSION,
        themeId: currentThemeId,
      });
      const mediaUsage = countPageMedia(limitedPageData);
      if (
        mediaUsage.images > PAGE_MEDIA_LIMITS.images ||
        mediaUsage.videos > PAGE_MEDIA_LIMITS.videos ||
        mediaUsage.audios > PAGE_MEDIA_LIMITS.audios ||
        mediaUsage.total > PAGE_MEDIA_LIMITS.total
      ) {
        await feedback.alert({
          type: 'warning',
          title: 'Media Limit Reached',
          message:
            `Current usage: ${mediaUsage.images}/${PAGE_MEDIA_LIMITS.images} photos, ` +
            `${mediaUsage.videos}/${PAGE_MEDIA_LIMITS.videos} videos, ${mediaUsage.audios}/${PAGE_MEDIA_LIMITS.audios} audio notes, ` +
            `${mediaUsage.total}/${PAGE_MEDIA_LIMITS.total} total files. Remove unused media before saving.`,
        });
        setIsLoading(false);
        return;
      }
      const savePayload = sanitizeForFirestore(limitedPageData);
      let saveResult = null;
      if (lockGift) {
        saveResult = await AdminAuthService.savePage(pageId, savePayload);
        saveStep = 'sealing gift';
        await AdminAuthService.lockPage(pageId);
        setIsLocked(true);
      } else {
        saveResult = await AdminAuthService.savePage(pageId, savePayload);
      }
      const cleanupMessage = getMediaCleanupMessage(saveResult?.mediaCleanup);
      if (lockGift) {
        setIsEditing(false);
        setIsPreviewMode(false);
        await AdminAuthService.logoutAdmin(pageId);
        feedback.toast({
          type: 'success',
          title: 'Gift Sealed',
          message: cleanupMessage.trim() || 'Your gift is ready to view.',
          duration: 5200,
        });
      } else {
        feedback.toast({
          type: 'success',
          title: 'Draft Saved',
          message: `You can keep editing or copy the Blessing Wall invite link now.${cleanupMessage}`,
          duration: 5200,
        });
      }
    } catch (error) {
      const code = error.code ? `${error.code}: ` : '';
      await feedback.alert({
        type: 'error',
        title: 'Save Failed',
        message: `Save failed while ${saveStep}: ${code}${error.message}`,
      });
      if (
        error.code === 'permission-denied' ||
        error.code === 'functions/permission-denied' ||
        String(error.message || '').toLowerCase().includes('expired')
      ) {
        await AdminAuthService.logoutAdmin(pageId);
        onAdminSessionExpired?.();
      }
    }
    setIsLoading(false);
  }, [
    currentThemeId,
    feedback,
    isUploadActive,
    onAdminSessionExpired,
    pageData,
    pageId,
    setIsEditing,
    setIsLoading,
    setIsLocked,
    setIsPreviewMode,
  ]);

  const deleteGift = useCallback(async () => {
    if (deleteInput.toUpperCase() !== 'DELETE') return;
    setIsLoading(true);
    try {
      const result = await AdminAuthService.deletePage(pageId);
      if (result?.storageCleanupComplete === false) {
        await feedback.alert({
          type: 'warning',
          title: 'Gift Reset',
          message: 'The gift was reset, but some uploaded files may need manual storage cleanup.',
        });
      }
      await AdminAuthService.logoutAdmin(pageId);
      onGiftDeleted?.(result);
      feedback.toast({
        type: 'success',
        title: 'Gift Reset',
        message: 'This link is ready to initialize again with the default password.',
        duration: 5200,
      });
    } catch (error) {
      await feedback.alert({
        type: 'error',
        title: 'Delete Failed',
        message: error.message,
      });
    }
    setIsLoading(false);
  }, [deleteInput, feedback, onGiftDeleted, pageId, setIsLoading]);

  const addModule = useCallback((type) => {
    if (isHiddenMainFlowModule(type)) {
      feedback.toast({
        type: 'info',
        title: 'Legacy Module Hidden',
        message: 'Use Photo Story, Letter, Voice, Blessing Wall, or Final Card instead.',
      });
      return null;
    }
    const sameTypeCount = pageData.modules.filter((module) => module.type === type).length;
    const maxInstances = MODULE_LIMITS[type] || 2;
    if (sameTypeCount >= maxInstances) {
      feedback.toast({
        type: 'warning',
        title: 'Module Limit',
        message: `This module can be added up to ${maxInstances} time${maxInstances === 1 ? '' : 's'}.`,
      });
      return null;
    }
    const newModule = createStoryModule(type, sameTypeCount, pageData);
    setPageData((prev) => ({ ...prev, modules: [...prev.modules, newModule] }));
    scrollToModuleEnd();
    return newModule.id;
  }, [feedback, pageData, scrollToModuleEnd, setPageData]);

  const updateStoryCoachAnswer = useCallback((key, value) => {
    setPageData((prev) => ({
      ...prev,
      storyCoachAnswers: {
        ...DEFAULT_STORY_COACH_ANSWERS,
        ...(prev.storyCoachAnswers || {}),
        [key]: value,
      },
    }));
  }, [setPageData]);

  const updateSharingMode = useCallback((shareMode) => {
    setPageData((prev) => ({
      ...prev,
      sharingSettings: {
        ...(prev.sharingSettings || {}),
        ...getShareSettingsForMode(shareMode),
        __touched: true,
      },
    }));
  }, [setPageData]);

  const applyStoryCoach = useCallback((item) => {
    const answer = (pageData.storyCoachAnswers?.[item.key] || '').trim();
    if (!answer) {
      feedback.toast({
        type: 'info',
        title: 'Story Coach',
        message: 'Write an answer first, then apply it to the story.',
      });
      return;
    }

    setPageData((prev) => {
      const existingIndex = prev.modules.findIndex((module) => module.type === item.suggestedModule);
      if (existingIndex >= 0) {
        return {
          ...prev,
          modules: prev.modules.map((module, index) => index === existingIndex ? {
            ...module,
            data: {
              ...getStoryCoachModuleData(item.key, answer, module.data || {}, prev),
              __touched: true,
            },
          } : module),
        };
      }

      const sameTypeCount = prev.modules.filter((module) => module.type === item.suggestedModule).length;
      const maxInstances = MODULE_LIMITS[item.suggestedModule] || 2;
      if (sameTypeCount >= maxInstances) return prev;

      return {
        ...prev,
        modules: [
          ...prev.modules,
          {
            id: `m_${Date.now()}`,
            type: item.suggestedModule,
            data: {
              ...getStoryCoachModuleData(item.key, answer, {}, prev),
              __touched: true,
            },
          },
        ],
      };
    });

  }, [feedback, pageData.storyCoachAnswers, setPageData]);

  const applyAllStoryCoach = useCallback(() => {
    const answers = pageData.storyCoachAnswers || {};
    const hasAnswer = Object.values(answers).some((value) => String(value || '').trim());
    if (!hasAnswer) {
      feedback.toast({
        type: 'info',
        title: 'Story Coach',
        message: 'Answer at least one Story Coach question first.',
      });
      return;
    }

    setPageData((prev) => applyStoryCoachAnswersToStory(prev));
  }, [feedback, pageData.storyCoachAnswers, setPageData]);

  return {
    saveGift,
    deleteGift,
    addModule,
    updateModule,
    removeModule,
    updateStoryCoachAnswer,
    updateSharingMode,
    applyStoryCoach,
    applyAllStoryCoach,
  };
};

export default useGiftActions;
